import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from 'api/api';
import TokenUtils from 'utils/TokenUtils';
import { addNotification } from './notificationsSlice';

export const getPayments = createAsyncThunk('payments/getPayments', async (params, { rejectWithValue }) => {
  try {
    const response = await api.get('/payments', { params });
    return { ...response.data, isLoadMore: params.page > 1 };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати рахунки');
  }
});

export const loadMorePayments = createAsyncThunk('payments/loadMorePayments', async (params, { rejectWithValue }) => {
  try {
    const response = await api.get('/payments', { params });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати рахунки');
  }
});

export const getPaymentByID = createAsyncThunk('payments/getPaymentByID', async (params, { rejectWithValue }) => {
  try {
    const response = await api.get('/payments', { params });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати рахунок');
  }
});

export const createPayment = createAsyncThunk(
  'payments/createPayment',
  async (paymentData, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.post('/payments', paymentData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Рахунок успішно створено',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка створення рахунку');
    }
  }
);

export const updatePayment = createAsyncThunk(
  'payments/updatePayment',
  async ({ id, ...updateData }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.put(`/payments/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Рахунок успішно оновлено',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка оновлення рахунку');
    }
  }
);

export const deletePayment = createAsyncThunk(
  'payments/deletePayment',
  async (paymentID, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      await api.delete(`/payments/${paymentID}`, { headers: { Authorization: `Bearer ${token}` } });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Рахунок видалено',
          type: 'success',
        })
      );

      return paymentID;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка видалення рахунку');
    }
  }
);

export const createMembershipFees = createAsyncThunk(
  'payments/createMembershipFees',
  async ({ clientID, amount, months }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.post(
        '/payments/membership-fees/manual',
        { clientID, amount, months },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { created, errors } = response.data;

      if (created.length > 0) {
        created.forEach(({ description }) => {
          dispatch(
            addNotification({
              id: Date.now() + 1,
              title: `Успішно створено: ${description}`,
              type: 'success',
            })
          );
        });
      }

      if (errors && errors.length > 0) {
        errors.forEach(({ month, error }) => {
          dispatch(
            addNotification({
              id: Date.now() + 1,
              title: `${error}: ${month}`,
              type: 'error',
            })
          );
        });
      }

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || 'Помилка створення членських внесків';

      dispatch(
        addNotification({
          id: Date.now(),
          title: errorMessage,
          type: 'error',
        })
      );

      return rejectWithValue(errorMessage);
    }
  }
);

const paymentsSlice = createSlice({
  name: 'payments',
  initialState: {
    payments: [],
    isLoading: false,
    isLoadingMore: false,
    error: null,
    meta: { total: 0, page: 1, limit: 30, totalPages: 0, hasMore: false },
    currentParams: null,
  },
  reducers: {
    addTransactionToPayment: (state, action) => {
      const { paymentID, transaction } = action.payload;
      const payment = state.payments.find((p) => p.id === paymentID);
      if (payment) {
        payment.transactions = payment.transactions || [];
        payment.transactions.push(transaction);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== getPayments =====
      .addCase(getPayments.pending, (state, action) => {
        const isLoadMore = action.meta.arg.page > 1;
        if (isLoadMore) state.isLoadingMore = true;
        else {
          state.isLoading = true;
          state.payments = [];
        }
        state.error = null;
      })
      .addCase(getPayments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        const { data, meta, isLoadMore } = action.payload;
        state.payments = isLoadMore ? [...state.payments, ...data] : data;
        state.meta = meta;
        state.currentParams = action.meta.arg;
      })
      .addCase(getPayments.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload;
      })

      // ===== loadMorePayments =====
      .addCase(loadMorePayments.pending, (state) => {
        state.isLoadingMore = true;
        state.error = null;
      })
      .addCase(loadMorePayments.fulfilled, (state, action) => {
        state.isLoadingMore = false;
        const { data, meta } = action.payload;
        state.payments = [...state.payments, ...data];
        state.meta = meta;
      })
      .addCase(loadMorePayments.rejected, (state, action) => {
        state.isLoadingMore = false;
        state.error = action.payload;
      })

      // ===== getPaymentByID =====
      .addCase(getPaymentByID.pending, (state) => {
        state.error = null;
      })
      .addCase(getPaymentByID.fulfilled, (state, action) => {
        const updatedPayment = action.payload.data[0];
        const index = state.payments.findIndex((p) => p.id === updatedPayment.id);

        if (index !== -1) {
          state.payments[index] = updatedPayment;
        } else {
          state.payments.push(updatedPayment);
        }
      })
      .addCase(getPaymentByID.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ===== createPayment =====
      .addCase(createPayment.fulfilled, (state, action) => {
        state.payments.unshift(action.payload);
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ===== updatePayment =====
      .addCase(updatePayment.fulfilled, (state, action) => {
        const index = state.payments.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) state.payments[index] = action.payload;
      })
      .addCase(updatePayment.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ===== deletePayment =====
      .addCase(deletePayment.fulfilled, (state, action) => {
        state.payments = state.payments.filter((p) => p.id !== action.payload);
      })
      .addCase(deletePayment.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ===== createMembershipFees =====
      .addCase(createMembershipFees.fulfilled, (state, action) => {
        if (action.payload.payments?.length > 0) {
          state.payments = [...action.payload.payments, ...state.payments];
        }
      })
      .addCase(createMembershipFees.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ===== reset error for pending actions =====
      .addMatcher(
        (action) =>
          (action.type.startsWith('payments/createPayment') ||
            action.type.startsWith('payments/updatePayment') ||
            action.type.startsWith('payments/deletePayment')) &&
          action.type.endsWith('/pending'),
        (state) => {
          state.error = null;
        }
      );
  },
});

export const { addTransactionToPayment } = paymentsSlice.actions;
export default paymentsSlice.reducer;
